import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import Loader from '../../../shared/components/Loader/Loader';
import useGlobalSearch from '../hooks/useGlobalSearch';
import GlobalSearchFilters from '../components/GlobalSearchFilters';
import GlobalSearchResult from '../components/GlobalSearchResult';
import GlobalSearchEmptyState from '../components/GlobalSearchEmptyState';
import { SEARCH_MODULE_LABELS } from '../models/globalSearchModel';
import { addSearchHistory } from '../utils/globalSearchHistory';

export default function GlobalSearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const urlQuery = params.get('q') || '';
  const activeType = params.get('types') || '';
  const state = useGlobalSearch({ mode: 'page', initialQuery: urlQuery, initialTypes: activeType ? [activeType] : [] });
  const [draft, setDraft] = useState(urlQuery);
  const [visibleLimit, setVisibleLimit] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);

  // URL is deliberately the only source of truth for this shareable page.
  useEffect(() => {
    setDraft(urlQuery);
    state.setQuery(urlQuery);
    state.setTypes(activeType ? [activeType] : []);
    setVisibleLimit(20);
    setLoadingMore(false);
    if (urlQuery.trim().length >= 2) state.search({ query: urlQuery, types: activeType ? [activeType] : [], limit: 20, offset: 0 });
    else state.reset();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, activeType]);

  const submit = (event) => {
    event?.preventDefault();
    const next = new URLSearchParams();
    if (draft.trim()) next.set('q', draft.trim());
    if (activeType) next.set('types', activeType);
    addSearchHistory(draft);
    setParams(next);
  };
  const filter = (type) => {
    const next = new URLSearchParams(params);
    if (type) next.set('types', type);
    else next.delete('types');
    setParams(next);
  };
  const loadMore = async () => {
    const limit = Math.min(100, visibleLimit + 20);
    setLoadingMore(true);
    try {
      await state.search({ query: urlQuery, types: activeType ? [activeType] : [], limit });
      setVisibleLimit(limit);
    } finally {
      setLoadingMore(false);
    }
  };
  const failed = Object.entries(state.providers || {})
    .filter(([, value]) => value === 'error')
    .map(([key]) => SEARCH_MODULE_LABELS[key] || key);
  const shownLabel = `${state.results.length} résultat${state.results.length !== 1 ? 's' : ''} affiché${state.results.length !== 1 ? 's' : ''}`;

  return <main className="global-search-page">
    <header className="global-search-page__header">
      <div><h1>Recherche dans l’ERP</h1><p>{state.loading && !loadingMore ? 'Recherche en cours…' : `${shownLabel}${urlQuery ? ` pour « ${urlQuery} »` : ''}`}</p></div>
      <form onSubmit={submit}><SearchIcon/><input aria-label="Rechercher dans l’ERP" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Projet, affaire, client, devis, Wiki…"/><button type="submit">Rechercher</button></form>
    </header>
    <GlobalSearchFilters value={activeType} groups={state.groups} total={state.total} onChange={filter}/>
    <div className="global-search-sort">Tri : <strong>Pertinence</strong></div>
    {failed.length > 0 && !state.error && <p className="global-search-partial">Certains modules n’ont pas pu être interrogés : {failed.join(', ')}.</p>}
    {state.loading && !loadingMore && <Loader label="Recherche dans l’ERP…"/>}
    {state.error && <div className="global-search-page__error"><p>Impossible d’effectuer la recherche.</p><button type="button" onClick={() => state.search({ query: urlQuery, types: activeType ? [activeType] : [], limit: visibleLimit })}>Réessayer</button></div>}
    {!state.loading && !state.error && !urlQuery && <GlobalSearchEmptyState/>}
    {!state.loading && !state.error && urlQuery && state.results.length === 0 && <GlobalSearchEmptyState query={urlQuery}/>}
    {(!state.loading || loadingMore) && !state.error && state.results.length > 0 && <section className="global-search-page__results" aria-label="Résultats">{state.results.map((result) => <GlobalSearchResult key={result.id} result={result} query={urlQuery} option={false} onSelect={(item) => navigate(item.url)}/>)}</section>}
    {!state.error && state.total > state.results.length && visibleLimit < 100 && <button className="global-search-load-more" type="button" disabled={loadingMore} onClick={loadMore}>{loadingMore ? 'Chargement…' : 'Afficher plus'}</button>}
    <p className="global-search-limit-note">Les résultats disponibles sont limités par module pour garantir un temps de réponse stable.</p>
  </main>;
}
